/**
 * PatientDataCard — the shared "Patient Data" card shell.
 *
 * One source of truth for the identity card chrome so the editable (no-INS) and
 * locked (INSi) identity sections look aligned: same bordered card, same
 * "Patient Data" header, same field grid inside. The variants differ only in:
 *   - editable → neutral grey border, no badge/banner.
 *   - locked   → blue border, INSi badge + "identity locked" banner + footer.
 *
 * The body (the actual fields) is passed as `children` — editable form fields or
 * read-only locked fields — so this component stays purely presentational.
 */
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { Box as MuiBox, Typography } from '@mui/material';

import colors from 'lib/colors';

export default function PatientDataCard({
  variant,
  badge,
  banner,
  footer,
  dataId,
  accent,
  accentLight,
  children,
}) {
  const { t } = useTranslation();
  const locked = variant === 'locked';

  // Accent is published as CSS variables on the card root so the border, badge,
  // banner, INS number and footer (all descendants) pick it up without threading
  // the colour through every styled element. Defaults keep the original blue.
  const accentVars = locked
    ? { '--insi-accent': accent, '--insi-light': accentLight }
    : undefined;

  return (
    <SCard $locked={locked} data-id={dataId} style={accentVars}>
      <SHeader>
        <Typography className="title">{t('Patient Data')}</Typography>
        {badge && <span className="badge">INSi</span>}
      </SHeader>

      {banner || null}

      <MuiBox className="body">{children}</MuiBox>

      {footer || null}
    </SCard>
  );
}

PatientDataCard.propTypes = {
  variant: PropTypes.oneOf(['editable', 'locked']),
  badge: PropTypes.bool,
  banner: PropTypes.node,
  footer: PropTypes.node,
  dataId: PropTypes.string,
  /** Locked-variant accent (status colour) + its light tint. */
  accent: PropTypes.string,
  accentLight: PropTypes.string,
  children: PropTypes.node,
};

// Locked-variant defaults (Récupérée blue) — overridden per status via props.
const BLUE = colors.blue100;
const BLUE_LIGHT = colors.blue200;
const NEUTRAL_BORDER = colors.gray300;

PatientDataCard.defaultProps = {
  variant: 'editable',
  badge: false,
  banner: null,
  footer: null,
  dataId: undefined,
  accent: BLUE,
  accentLight: BLUE_LIGHT,
  children: null,
};

const SCard = styled(MuiBox)`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 16px;
  width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid
    ${({ $locked }) => ($locked ? `var(--insi-accent, ${BLUE})` : NEUTRAL_BORDER)};

  .body {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
`;

const SHeader = styled(MuiBox)`
  display: flex;
  align-items: center;
  gap: 8px;

  .title {
    font-weight: 600;
    font-size: 20px;
    line-height: 27px;
    color: #222222;
  }
  .badge {
    padding: 2px 6px;
    border-radius: 20px;
    background: var(--insi-light, ${BLUE_LIGHT});
    color: var(--insi-accent, ${BLUE});
    font-weight: 600;
    font-size: 14px;
    line-height: 17px;
  }
`;
